import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Ban, CalendarRange, Repeat } from "lucide-react";

const WEEKDAYS = [
  { i: 0, label: "Dom", full: "Domingo" },
  { i: 1, label: "Seg", full: "Segunda" },
  { i: 2, label: "Ter", full: "Terça" },
  { i: 3, label: "Qua", full: "Quarta" },
  { i: 4, label: "Qui", full: "Quinta" },
  { i: 5, label: "Sex", full: "Sexta" },
  { i: 6, label: "Sáb", full: "Sábado" },
];

const emptyForm = () => ({
  title: "",
  start_time: "12:00",
  end_time: "13:00",
  professional_id: "__all__",
  kind: "recurring",
  weekdays: [1, 2, 3, 4, 5],
  date: "",
  active: true,
});

export default function TimeBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const proMap = useMemo(() => Object.fromEntries(pros.map((p) => [p.id, p.name])), [pros]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([api.get("/time-blocks"), api.get("/professionals")]);
      setBlocks(b.data || []);
      setPros((p.data || []).filter((x) => x.active !== false));
    } catch (err) {
      toast.error(errMsg(err, "Falha ao carregar bloqueios"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title || "",
      start_time: b.start_time,
      end_time: b.end_time,
      professional_id: b.professional_id || "__all__",
      kind: b.date ? "one_off" : "recurring",
      weekdays: b.weekdays || [],
      date: b.date || "",
      active: b.active !== false,
    });
    setOpen(true);
  };

  const toggleWd = (i) => {
    setForm((f) => ({ ...f, weekdays: f.weekdays.includes(i) ? f.weekdays.filter((w) => w !== i) : [...f.weekdays, i].sort() }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Dê um nome ao bloqueio (ex.: Almoço)"); return; }
    if (form.kind === "recurring" && form.weekdays.length === 0) { toast.error("Selecione pelo menos um dia da semana"); return; }
    if (form.kind === "one_off" && !form.date) { toast.error("Escolha uma data"); return; }
    const payload = {
      title: form.title.trim(),
      start_time: form.start_time,
      end_time: form.end_time,
      professional_id: form.professional_id === "__all__" ? null : form.professional_id,
      weekdays: form.kind === "recurring" ? form.weekdays : [],
      date: form.kind === "one_off" ? form.date : null,
      active: form.active,
    };
    try {
      if (editingId) {
        await api.put(`/time-blocks/${editingId}`, payload);
        toast.success("Bloqueio atualizado");
      } else {
        await api.post("/time-blocks", payload);
        toast.success("Bloqueio criado");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(errMsg(err, "Falha ao salvar"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/time-blocks/${id}`);
      toast.success("Bloqueio removido");
      load();
    } catch (err) { toast.error(errMsg(err, "Falha ao remover")); }
  };

  const describe = (b) => {
    if (b.date) {
      const [y, m, d] = b.date.split("-");
      return `${d}/${m}/${y}`;
    }
    if (!b.weekdays || b.weekdays.length === 0) return "Todos os dias";
    if (b.weekdays.length === 7) return "Todos os dias";
    return b.weekdays.map((i) => WEEKDAYS.find((w) => w.i === i)?.label).join(", ");
  };

  return (
    <div className="space-y-6" data-testid="time-blocks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Bloqueios de horário</h1>
          <p className="text-sm text-muted-foreground">Bloqueie faixas na agenda (almoço, folga, treinamento…). Vale para o `/agendar` público e para o painel.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-block" onClick={openNew} className="h-11"><Plus className="h-4 w-4 mr-2" /> Novo bloqueio</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar bloqueio" : "Novo bloqueio"}</DialogTitle>
              <DialogDescription>Defina uma faixa em que ninguém pode agendar. Use "recorrente" para o mesmo horário em vários dias, ou "pontual" para uma data específica.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input data-testid="input-block-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex.: Almoço, Reunião, Consulta médica" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Início</Label>
                  <Input data-testid="input-block-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Fim</Label>
                  <Input data-testid="input-block-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Aplicar a</Label>
                <Select value={form.professional_id} onValueChange={(v) => setForm({ ...form, professional_id: v })}>
                  <SelectTrigger data-testid="select-block-pro"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os profissionais</SelectItem>
                    {pros.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant={form.kind === "recurring" ? "default" : "outline"} onClick={() => setForm({ ...form, kind: "recurring" })} data-testid="btn-block-recurring">
                    <Repeat className="h-4 w-4 mr-2" /> Recorrente
                  </Button>
                  <Button type="button" variant={form.kind === "one_off" ? "default" : "outline"} onClick={() => setForm({ ...form, kind: "one_off" })} data-testid="btn-block-oneoff">
                    <CalendarRange className="h-4 w-4 mr-2" /> Pontual
                  </Button>
                </div>
              </div>
              {form.kind === "recurring" ? (
                <div className="space-y-2">
                  <Label>Dias da semana</Label>
                  <div className="grid grid-cols-7 gap-1">
                    {WEEKDAYS.map((w) => (
                      <Button key={w.i} type="button" size="sm" variant={form.weekdays.includes(w.i) ? "default" : "outline"} onClick={() => toggleWd(w.i)} data-testid={`btn-wd-${w.i}`} className="text-xs">{w.label}</Button>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button type="button" size="sm" variant="ghost" data-testid="btn-wd-weekdays" onClick={() => setForm({ ...form, weekdays: [1, 2, 3, 4, 5] })}>Seg–Sex</Button>
                    <Button type="button" size="sm" variant="ghost" data-testid="btn-wd-all" onClick={() => setForm({ ...form, weekdays: [0, 1, 2, 3, 4, 5, 6] })}>Todos</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input data-testid="input-block-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch data-testid="switch-block-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label>Ativo</Label>
                <span className="text-xs text-muted-foreground ml-auto">Desative para pausar sem apagar.</span>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-block" onClick={save}>{editingId ? "Salvar alterações" : "Criar bloqueio"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface p-1 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
        ) : blocks.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Ban className="h-8 w-8 mx-auto mb-2 opacity-60" />
            Nenhum bloqueio criado ainda. Clique em "Novo bloqueio" para começar (ex.: Almoço 12:00–13:00 seg a sex).
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="p-3">Título</th>
                <th className="p-3 hidden sm:table-cell">Profissional</th>
                <th className="p-3">Faixa</th>
                <th className="p-3 hidden md:table-cell">Quando</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.id} className={`border-b border-border/60 ${!b.active ? "opacity-50" : ""}`} data-testid={`block-row-${b.id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-2">
                      {b.title}
                      {!b.active && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">pausado</span>}
                    </div>
                    <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                      {b.professional_id ? proMap[b.professional_id] || "—" : "Todos"}
                    </div>
                  </td>
                  <td className="p-3 hidden sm:table-cell">{b.professional_id ? (proMap[b.professional_id] || <span className="text-muted-foreground">—</span>) : <span className="text-muted-foreground">Todos</span>}</td>
                  <td className="p-3 font-mono">{b.start_time} – {b.end_time}</td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground">{describe(b)}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`btn-edit-block-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="ml-2" data-testid={`btn-del-block-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover bloqueio?</AlertDialogTitle>
                          <AlertDialogDescription>“{b.title}” será removido definitivamente. Você pode recriar depois.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction data-testid={`btn-confirm-del-block-${b.id}`} onClick={() => remove(b.id)}>Remover</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
